import { useEffect } from 'react';

/**
 * On dashboard.kizu.online, redirect mobile users to mobile.kizu.online.
 * Preserves the current path so /notifications → mobile.kizu.online/mobile/home
 * (or specific deep link mappings).
 *
 * Skipped for:
 * - PWA standalone mode
 * - ?force=desktop query param
 * - localhost / dev environments
 * - Public routes that work on both (album viewer, invite pages, etc.)
 */
export function useMobileRedirect() {
  useEffect(() => {
    const hostname = window.location.hostname;

    // Only redirect on the production/dev dashboard domains
    const isDashboard = hostname === 'dashboard.kizu.online' || hostname === 'dev.dashboard.kizu.online';
    if (!isDashboard) return;

    // Skip when the page is rendered inside the React Native WebView. The
    // mobile app loads dashboard.kizu.online directly and relies on the
    // native postMessage bridge for "Done" to close the modal. Bouncing to
    // mobile.kizu.online breaks that bridge (different origin, WebView
    // punts the URL to Safari).
    if ((window as any).ReactNativeWebView) return;

    // Skip if ?force=desktop
    const params = new URLSearchParams(window.location.search);
    if (params.get('force') === 'desktop') return;

    // Skip in PWA standalone mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;
    if (isStandalone) return;

    // Skip for public routes that should work on both desktop and mobile
    const publicPaths = ['/album/', '/view-circle/', '/join-circle/', '/join-circle-web/', '/start-circle/', '/frame-setup/', '/frame', '/share', '/asset/', '/shared-album/', '/invite/', '/unsubscribe', '/album-invite/', '/login', '/signup', '/connect/', '/i/', '/j/', '/events/'];
    const path = window.location.pathname;
    if (publicPaths.some(p => path.startsWith(p))) return;

    // Detect mobile via user agent (more reliable than media query for this)
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Build mobile URL — preserve path and query string
    const mobileHost = hostname.replace('dashboard.', 'mobile.');
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    // Map dashboard paths to mobile paths, or default to /mobile/home
    const mobilePath = currentPath === '/' ? '/mobile/home' : currentPath;
    const mobileUrl = `${window.location.protocol}//${mobileHost}${mobilePath}${currentSearch}`;

    window.location.href = mobileUrl;
  }, []);
}
